import {Component, DoCheck, OnInit} from '@angular/core';
import {ControlContainer, FormsModule, NgForm, NgModel} from '@angular/forms';
import {NgClass} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {NgSelectModule} from '@ng-select/ng-select';
import {AttachmentValidationService} from '../../common/service/attachment-validation.service';
import {getMimeType} from '../../common/service/mime-type.utils';
import {LookupService} from '../../common/service/lookup.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {PermissionsService} from '../../common/service/permissions.service';
import {AppConfigService} from '../../common/service/app-config.service';
import {AlertModalService, AttachmentsModalService} from '../../common/component/modal';
import {NgEventBus} from 'ng-event-bus';
import {AttachmentTypeLookup} from '../../common/models/attachment-type.model';
import {ATTACHMENT_CSD, ATTACHMENT_PLAN_PDF, LAND_CSV, NLR_TABS, PDF_CREATION_METHOD} from '../../common/constants';
import {concatMap, first, Observable, of, switchMap, tap} from 'rxjs';
import {AttachmentModel} from '../../common/models/attachment-model';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {AttachmentConfiguration} from '../../common/models/attachment-configuration.model';
import {PlanAttachment} from '../../common/models/plan-attachment.model';
import {FileInfo} from '../../common/models/attachment-file-info.model';
import {EvidenceValidationService} from '../../common/component/evidence-validation/evidence-validation.service';
import {EvidenceValidationComponent} from '../../common/component/evidence-validation/evidence-validation.component';
import {PlanLodgementService} from '../../common/service/plan-lodgement.service';
import {NlrPlanDetailService} from '../../plan-detail/plan-detail.service';
import {AttachmentValidatorDirective} from './attachment-validator.directive';

@Component({
    selector: 'attachments',
    imports: [
        FormsModule,
        NgClass,
        NlrHasPermissionsPipe,
        EvidenceValidationComponent,
        AttachmentValidatorDirective,
        NgSelectModule
    ],
    templateUrl: './attachments.component.html',
    styleUrl: './attachments.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class AttachmentsComponent implements OnInit, DoCheck {
    readonly permissions = ['VIEW_LODGE', 'EDIT_LODGE'];

    title = 'Attachments';

    apiGatewayUrl = '';

    attachmentTypeLookups: AttachmentTypeLookup[] = [];

    attachmentConfigurationMap: Map<string, AttachmentConfiguration> = new Map();

    readonly maxAttachmentsFileSize = 10485760;

    private lotSyncAtLastCheck: boolean;

    private attachmentTypeOptionalitySummaryAtLastCheck: string;

    constructor(
        private ngForm: NgForm,
        private eventBus: NgEventBus,
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private attachmentsModalService: AttachmentsModalService,
        private attachmentValidationService: AttachmentValidationService,
        private evidenceValidationService: EvidenceValidationService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private permissionsService: PermissionsService,
        private alertModalService: AlertModalService,
        private nlrPlanDetailService: NlrPlanDetailService
    ) {}

    ngOnInit() {
        this.apiGatewayUrl = this.appConfigService.settings.apiGatewayUrl;
        this.attachmentValidationService.clearAllValidationInfo();
        this.attachmentTypeLookups = this.lookupService.getLookups('attachmentType');
        this.lotSyncAtLastCheck = this.getModel().lotSync;
        this.attachmentTypeOptionalitySummaryAtLastCheck = this.getAttachmentTypeOptionalitySummary();
    }

    ngDoCheck() {
        const attachmentConfigurations = this.planLodgementService.getAttachmentConfigurations();
        if (
            this.attachmentConfigurationMap.size !== attachmentConfigurations.length ||
            !attachmentConfigurations.every(ac => ac === this.attachmentConfigurationMap.get(ac.attachmentType))
        ) {
            this.attachmentConfigurationMap.clear();
            attachmentConfigurations.forEach(ac => this.attachmentConfigurationMap.set(ac.attachmentType, ac));
        }

        if (this.lotSyncAtLastCheck !== this.getModel().lotSync) {
            this.lotSyncAtLastCheck = this.getModel().lotSync;
            this.planLodgementService.updateAttachments(false).pipe(first()).subscribe();
        }

        const attachmentTypeOptionalitySummary = this.getAttachmentTypeOptionalitySummary();
        if (this.attachmentTypeOptionalitySummaryAtLastCheck !== attachmentTypeOptionalitySummary) {
            this.attachmentTypeOptionalitySummaryAtLastCheck = attachmentTypeOptionalitySummary;
            this.eventBus.cast('updatePlanLots');
        }
    }

    isDisabled() {
        return this.planModelService.readonly || !this.permissionsService.hasEditPermission(this.permissions);
    }

    getModel() {
        return this.planModelService.getModel();
    }

    getAttachments() {
        return this.getModel().attachments || [];
    }

    getAttachmentConfigurations() {
        return this.planLodgementService.getAttachmentConfigurations();
    }

    getAttachmentConfiguration(attachment: PlanAttachment) {
        return this.attachmentConfigurationMap.get(attachment.attachmentType);
    }

    getFilteredAttachments() {
        return this.getAttachments().filter(attachment => attachment.optionality !== 'Conditional');
    }

    trackByIndex(index: number, attachment: PlanAttachment): any {
        return index;
    }

    getDisabledErrorMessage(attachment: PlanAttachment) {
        const attachmentConfiguration = this.attachmentConfigurationMap.get(attachment.attachmentType);
        return attachmentConfiguration?.disabledErrorMessage || '';
    }

    multipleAttachmentsAllowed(attachment: PlanAttachment) {
        const attachmentConfiguration = this.attachmentConfigurationMap.get(attachment.attachmentType);
        return !!attachmentConfiguration?.multipleAttachments;
    }

    getAttachmentTypeOptions() {
        return this.getAttachmentConfigurations()
            .filter(ac => ac.multipleAttachments)
            .map(ac => ac.attachmentType);
    }

    attachmentTypeTrackBy(option) {
        return option;
    }

    getAttachmentsFileSize() {
        return this.getAttachments().reduce((acc, attachment) => acc + (attachment.fileSize || 0), 0);
    }

    hasError(attachment: PlanAttachment) {
        if (this.fileNowValid(attachment)) {
            return false;
        } else {
            return this.fileNowInvalid(attachment);
        }
    }

    fileNowValid(attachment: PlanAttachment) {
        return !this.isDisabled() && !!attachment.fileLocation && !this.fileMissingOrProhibited(attachment);
    }

    fileNowInvalid(attachment: PlanAttachment) {
        return !this.isDisabled() && this.ngForm.submitted && this.fileMissingOrProhibited(attachment);
    }

    /**
     * Taken from the attachment rather than the form control holding its file location. The attachments are re-sorted by
     * optionality while the rows are tracked and the controls named by index, so a control keeps the outcome it was given
     * for the attachment that used to occupy its row, which marks the wrong rows once the order changes. The callers keep
     * the disabled check that came with reading the control, a disabled control being neither valid nor invalid.
     */
    fileMissingOrProhibited(attachment: PlanAttachment) {
        if (attachment.optionality === 'Mandatory') {
            return !attachment.fileLocation;
        }

        return attachment.optionality === 'Prohibited';
    }

    attachmentNowValid(attachmentDetails: NgModel) {
        return attachmentDetails.valid && attachmentDetails.dirty;
    }

    attachmentNowInvalid(attachmentDetails: NgModel) {
        return attachmentDetails.invalid && attachmentDetails.dirty;
    }

    attachmentNowMissing(attachmentDetails: NgModel) {
        return attachmentDetails.errors?.['required'] && attachmentDetails.dirty;
    }

    addBlankAttachment() {
        this.getAttachments().push({createdByUser: true, optionality: 'Optional'});
    }

    triggerClear(attachment: PlanAttachment) {
        attachment.fileLocation = undefined;
        attachment.fileName = undefined;
        attachment.fileSize = undefined;
        attachment.attachmentDetails = undefined;

        if (attachment.attachmentType === ATTACHMENT_CSD || attachment.attachmentType === LAND_CSV) {
            this.attachmentValidationService.clearValidationInfo(attachment.attachmentType);
        }

        this.eventBus.cast('updatePlanLots');

        this.planModelService.updateAttachments();
    }

    canPreview(filename: string) {
        if (!filename) {
            return false;
        }
        const extension = filename.substring(filename.lastIndexOf('.')).toUpperCase();
        return ['.CSD', '.TXT', '.XYZ', '.PTS', '.PDF', '.CSV', '.DOC', '.DOCX', '.DWG', '.MSG'].includes(extension);
    }

    getAttachmentFromSignedUrl(signedUrl: string): Observable<ArrayBuffer> {
        return this.http.get(signedUrl, {responseType: 'arraybuffer'});
    }

    triggerPreview(attachment: PlanAttachment) {
        const uidWithPrefix = attachment.fileLocation.substring(attachment.fileLocation.lastIndexOf('/Other/'));

        this.http
            .get(`${this.apiGatewayUrl}/attachment/signedUrlForRetrieval${uidWithPrefix}`)
            .pipe(
                concatMap((res: FileInfo) => {
                    if (!res) {
                        console.error(
                            'The attachment data could not be found. ' +
                                'In non-production environments, this can indicate that the attachment has expired.'
                        );
                        return null;
                    } else {
                        return this.getAttachmentFromSignedUrl(res.url);
                    }
                })
            )
            .subscribe((data: ArrayBuffer) => {
                const file = new Blob([data], getMimeType(attachment.fileName));
                const fileURL = URL.createObjectURL(file);

                const params = [
                    'height=' + screen.height,
                    'width=' + screen.width,
                    'fullscreen=yes' // only works in IE, but here for completeness
                ].join(',');

                const popup = window.open(fileURL, '_blank', params);
                popup.addEventListener('load', function () {
                    popup.document.title = attachment.fileName;
                });
                popup.moveTo(0, 0);

                popup.onload = function () {
                    URL.revokeObjectURL(fileURL);
                };
            });
    }

    triggerAttachFile(attachment: PlanAttachment, attachmentIndex: number) {
        const logPrefix = `${this.title} - triggerAttachFile - index: ${attachmentIndex}`;
        console.debug(logPrefix);

        const currentAttachmentConfiguration = this.getAttachmentConfigurations().filter(function (config) {
            return config.attachmentType === attachment.attachmentType;
        })[0];

        // signatures should be checked for Plan PDF attachments when the certification tab is displayed
        const checkPdfSignature =
            currentAttachmentConfiguration.attachmentType === ATTACHMENT_PLAN_PDF &&
            [PDF_CREATION_METHOD.SURVEYOR_PORTAL, PDF_CREATION_METHOD.DIGITAL_PLAN].includes(this.getModel().pdfCreationMethod);

        this.attachmentsModalService
            .showModal(currentAttachmentConfiguration.extensions, 'Other', this.getAttachments(), checkPdfSignature)
            .subscribe({
                next: (data: AttachmentModel | string) => {
                    if (typeof data === 'string') {
                        console.debug(this.title + ' : cancel');
                    } else {
                        if (data !== undefined) {
                            this.planModelService.updateAttachments();
                            this.validateAttachment(attachment, attachmentIndex, data);
                        }
                    }
                },
                error: () => {
                    console.debug(`${logPrefix} - closed without uploading`);

                    if (!attachment.fileLocation || !attachment.fileName) {
                        this.attachmentValidationService.clearFileInformation(attachment);
                    }
                }
            });
    }

    validateAttachment(attachment: PlanAttachment, attachmentIndex: number, newFileInformation: AttachmentModel) {
        if (newFileInformation !== undefined) {
            const convertToCadPlan = this.attachmentValidationService.requiresCadPlanConversion(attachment);

            if (convertToCadPlan && !this.planLodgementService.isCoordinateSystemValidForPdfCreationMethod(PDF_CREATION_METHOD.CAD_SOFTWARE)) {
                // Checked before the file is attached, as the plan cannot be converted and so cannot hold this Plan PDF.
                this.planLodgementService.showInvalidCoordinateSystemMessage(PDF_CREATION_METHOD.CAD_SOFTWARE);
                return;
            }

            this.planModelService.updateAttachments();

            const subscription = this.attachmentValidationService
                .validateAttachment(attachment, `${attachmentIndex}`, newFileInformation)
                .pipe(switchMap(() => (convertToCadPlan ? this.convertToCadPlan() : of(null))))
                .subscribe(() => this.eventBus.cast('updatePlanLots'));

            this.eventBus.cast('nlr:busy', subscription);
        }
    }

    /**
     * Converts the plan to a CAD plan, the Plan PDF just attached being one the system did not produce. Runs after the file
     * information is on the attachment, so the plan keeps the newly attached PDF rather than the one it replaces.
     */
    private convertToCadPlan(): Observable<unknown> {
        const wasDigitalPlan = this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN;

        this.planLodgementService.convertToCadPlan();
        this.planLodgementService.resetLodgementDetails();
        this.nlrPlanDetailService.resetValidationResults();

        this.eventBus.cast('nlr:plan-model-updated:pdfCreationMethod');

        return this.planLodgementService.updateAttachments(true).pipe(
            switchMap(() => (wasDigitalPlan ? this.planLodgementService.cancelDigitalPlanValidation() : of(null))),
            /**
             * The tab set is keyed on the plan's PDF creation method and lodgement stage, so tabs have come and gone. It holds
             * the selected tab as a position, which the removals have shifted, so re-select this tab to have that position
             * resolved again. Runs once the attachment refresh has returned, by which point the tabs have been re-rendered.
             */
            tap(() => this.eventBus.cast('nlr:selectTab', NLR_TABS.LODGE))
        );
    }

    removeAttachment(attachmentIndex: number) {
        const logPrefix = `${this.title} - removeAttachment`;
        console.debug(`${logPrefix} - index: ${attachmentIndex}`);

        const attachment = this.getAttachments()[attachmentIndex];
        console.debug(`${logPrefix} - attachment:`, attachment);

        this.getAttachments().splice(attachmentIndex, 1);
        this.planModelService.updateAttachments();
    }

    showDisabledErrorMessage(message: string) {
        this.alertModalService.createAlertMessage(message, 'Cancel');
    }

    getAttachmentName(attachmentType: string) {
        return this.attachmentTypeLookups.find(v => v.id === attachmentType)?.name || '';
    }

    hasValidationErrors(attachment: PlanAttachment) {
        return this.evidenceValidationService.hasValidationErrors(attachment);
    }

    hasValidationWarnings(attachment: PlanAttachment) {
        return this.evidenceValidationService.hasValidationWarnings(attachment);
    }

    hasValidationErrorsOrWarnings(attachment: PlanAttachment) {
        return this.evidenceValidationService.hasValidationErrorsOrWarnings(attachment);
    }

    private getAttachmentTypeOptionalitySummary() {
        return this.getAttachments()
            .map(a => `${a.attachmentType}:${a.optionality}`)
            .join();
    }
}
